import React, { useContext, useEffect } from 'react';
import { usePlaidLink } from 'react-plaid-link';

import { AnalyticsContext } from '../../../../../ModularReduxAnalytics';
import { useApp, useAppDispatchers } from '../../../../../state/app';
import { useToggles } from '../../../../../state/toggles';

import { usePlaidLinkShouldOpen } from './usePlaidLinkShouldOpen';
import { useCaptureErrorLoadingPlaid } from './useCaptureErrorLoadingPlaid';

import PlaidLink from './index';

interface PlaidLinkContainerProps {
  receivedRedirectUri: string | undefined;
  isMorganStanleyFlow: boolean;
  plaidSuccess: any;
  plaidExit: any;
  plaidEvent: any;
}

export default function PlaidLinkContainer({
  receivedRedirectUri,
  isMorganStanleyFlow,
  plaidSuccess,
  plaidExit,
  plaidEvent
}: PlaidLinkContainerProps) {
  const { analytics } = useContext(AnalyticsContext);
  const { createError, clearError, launchNativePlaidFlow } =
    useAppDispatchers();

  const { plaidLinkToken, embeddedWebreg } = useApp();

  const { open, ready, error, exit } = usePlaidLink({
    token: plaidLinkToken || null,
    receivedRedirectUri,
    onSuccess: plaidSuccess,
    onExit: plaidExit,
    onEvent: plaidEvent
  });

  // In cases where we're automatically redirected into plaid by OAuth,
  // we want to trigger an open() right away to continue with the flow.
  usePlaidLinkShouldOpen(ready, open, receivedRedirectUri);

  // In the case of an error in plaid loading, let the user know and then catch that error
  useCaptureErrorLoadingPlaid(error, createError);

  const openWithTracking = () => {
    analytics.trackEvent('Add Instant Bank Clicked', {
      eventProps: {
        during_registration: true
      }
    });

    if (embeddedWebreg) {
      launchNativePlaidFlow();
    }

    analytics.trackRUMEvent('Using PlaidLinkToken to open Plaid', {
      eventProps: { plaidLinkToken }
    });

    clearError();
    open();
  };

  useEffect(() => {
    return () => {
      // Close any open plaid modal when this components unmounts
      exit({ force: true });
    };
  }, []);

  return (
    <PlaidLink
      ready={ready}
      isMorganStanleyFlow={isMorganStanleyFlow}
      receivedRedirectUri={receivedRedirectUri as string}
      openWithTracking={openWithTracking}
    />
  );
}
